import React from "react";
import type { ILineStatusItem, LineStatusValue } from "../../../../lib/backend/manufacturing2.generated.types";
import LineStatusDetails from "../../LineStatusDetails";
import Tooltip from "../../../Tooltip";
import { classNames } from "../../../../lib/Util";

type Props = {
    status: ILineStatusItem | Error;
    cx?: number;
    cy?: number;
    r?: number;
    className?: string;
    /** When true, wraps the circle in an SVG for use outside an existing SVG context. */
    standaloneSvg?: boolean;
    /** When false, renders the status dot without a hover tooltip. Defaults to true. */
    showTooltip?: boolean;
};

const DEFAULT_R = 5;

/** Semantic CSS custom properties from vars.scss (--color-*). */
const LINE_STATUS_FILL: Record<LineStatusValue, string> = {
    stopped: "var(--color-error)",
    idle: "var(--color-warning)",
    working: "var(--color-success)",
    in_preparation: "var(--color-info)",
    unknown: "var(--color-neutral)",
};

const STALE_OPACITY = 0.5;

function getStatusFill(status: LineStatusValue): string {
    return LINE_STATUS_FILL[status] ?? LINE_STATUS_FILL.unknown;
}

type StatusDotProps = {
    cx?: number;
    cy?: number;
    r: number;
    fill: string;
    opacity?: number;
    standaloneSvg?: boolean;
} & React.SVGProps<SVGCircleElement>;

const StatusDot = ({ cx, cy, r, fill, opacity = 1, standaloneSvg = true, ...circleProps }: StatusDotProps) => {
    const circleCx = cx ?? r / 2;
    const circleCy = cy ?? r / 2;
    const circleR = standaloneSvg ? r / 2 : r;
    const strokeWidth = standaloneSvg ? 4 : 12;

    return (
        <circle
            className="cursor-default"
            cx={circleCx}
            cy={circleCy}
            r={circleR}
            fill={fill}
            // Increase the hit radius for easier mouse hover detection
            stroke="transparent"
            strokeWidth={strokeWidth}
            opacity={opacity}
            {...circleProps}
        />
    );
};

const LineStatus = (props: Props) => {
    const { status, cx, cy, r = DEFAULT_R, standaloneSvg = true, showTooltip = true, className } = props;

    let fill = "";
    let opacity = 1;
    let tooltipKey = "";
    let tooltipContent: React.ReactNode = "";

    if (status instanceof Error) {
        fill = "black";
        tooltipKey = `tooltip-error`;
        tooltipContent = status.message;
    } else {
        const live = status.live;
        fill = getStatusFill(live.status);
        opacity = live.stale ? STALE_OPACITY : 1;
        tooltipContent = <LineStatusDetails status={status} />;
        tooltipKey = `${status.lineUuid}-${live.statusTimestamp ?? ""}-${live.receivedAt ?? ""}`;
    }

    const statusDot = <StatusDot cx={cx} cy={cy} r={r} fill={fill} opacity={opacity} standaloneSvg={standaloneSvg} />;

    const svgContent = showTooltip ? (
        <Tooltip key={tooltipKey} content={tooltipContent}>
            {statusDot}
        </Tooltip>
    ) : (
        statusDot
    );

    if (standaloneSvg) {
        return (
            <svg
                width={r}
                height={r}
                viewBox={`0 0 ${r} ${r}`}
                className={classNames("line-status-inline", className)}
                aria-hidden
            >
                {svgContent}
            </svg>
        );
    }

    return svgContent;
};

export default LineStatus;
